import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import {
  FaHome,
  FaBoxOpen,
  FaInfoCircle,
  FaEnvelope,
  FaChevronDown,
  FaTimes,
} from 'react-icons/fa';
import { FiBox, FiMenu, FiArrowRight } from 'react-icons/fi';
import axios from 'axios';
import ButtonPanier from '../../components/Boutton/ButtonPanier/ButtonPanier';
import Search from '../../components/Search/Search';
import SearchIcon from '../../components/Search/SearchIcon';
import Logo from './Logo';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/bodyScrollLock';
import { getSubcategoryPath } from '../../utils/subcategoryRoutes';
import './navbar.css';

const megaMenuVariants = {
  hidden: { opacity: 0, y: -8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15, ease: 'easeIn' } },
};

const subListVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.03 } },
};

const subItemVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
};

const drawerVariants = {
  hidden: { x: '100%' },
  visible: { x: 0, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } },
  exit: { x: '100%', transition: { duration: 0.25, ease: 'easeIn' } },
};

const linkStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

const linkItem = {
  hidden: { opacity: 0, y: -6 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

const Navbarr = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [sousCategories, setSousCategories] = useState([]);
  const [activeParent, setActiveParent] = useState(null);

  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const [isMegaOpen, setIsMegaOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const [pill, setPill] = useState({ opacity: 0, x: 0, width: 0 });
  const [catIndicator, setCatIndicator] = useState({ opacity: 0, top: 0, height: 0 });

  const closeTimeoutRef = useRef(null);
  const navLinksRef = useRef(null);
  const headerRef = useRef(null);
  const catListRef = useRef(null);
  const catRowRefs = useRef({});

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/categorie/lister`);
        setCategories(res.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();

    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? Math.min(window.scrollY / docHeight, 1) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!activeParent) return;
    const fetchSousCategories = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/sous-categorie/lister`, {
          headers: { parent: activeParent },
        });
        setSousCategories(res.data);
      } catch (error) {
        console.error('Error fetching subcategories:', error);
      }
    };
    fetchSousCategories();
  }, [activeParent]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setIsMegaOpen(false);
        setIsMobileOpen(false);
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Lock body scroll whenever a full-screen overlay (drawer / search) is open.
  // Uses a shared ref-counted lock since the cart drawer locks independently too.
  const shouldLockScroll = isMobileOpen || isSearchOpen;
  useEffect(() => {
    if (!shouldLockScroll) return undefined;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [shouldLockScroll]);

  useEffect(() => {
    if (!isMegaOpen) return;
    const handleClickOutside = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setIsMegaOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMegaOpen]);

  const movePillTo = (target) => {
    if (!navLinksRef.current || !target) return;
    const parentRect = navLinksRef.current.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    setPill({ opacity: 1, x: rect.left - parentRect.left, width: rect.width });
  };

  const resetPill = () => setPill((p) => ({ ...p, opacity: 0 }));

  const moveCatIndicatorTo = (el) => {
    if (!catListRef.current || !el) return;
    const parentRect = catListRef.current.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    setCatIndicator({ opacity: 1, top: rect.top - parentRect.top, height: rect.height });
  };

  useEffect(() => {
    if (!isMegaOpen || !activeParent) return;
    const node = catRowRefs.current[activeParent];
    if (node) moveCatIndicatorTo(node);
  }, [isMegaOpen, activeParent]);

  const openMega = (e) => {
    clearTimeout(closeTimeoutRef.current);
    setIsMegaOpen(true);
    setActiveParent((current) => current || (categories[0] && categories[0].name) || null);
    if (e) movePillTo(e.currentTarget);
  };

  const scheduleCloseMega = () => {
    closeTimeoutRef.current = setTimeout(() => setIsMegaOpen(false), 250);
  };

  const cancelCloseMega = () => clearTimeout(closeTimeoutRef.current);

  const closeAll = () => {
    setIsMegaOpen(false);
    setIsMobileOpen(false);
    setIsMobileProductsOpen(false);
    setIsSearchOpen(false);
  };

  const handleChipSearch = (name) => {
    navigate(`/search/${encodeURIComponent(name)}`);
    setIsSearchOpen(false);
  };

  const toggleMobileProducts = () => {
    const next = !isMobileProductsOpen;
    setIsMobileProductsOpen(next);
    if (next) {
      setActiveParent((current) => current || (categories[0] && categories[0].name) || null);
    }
  };

  return (
    <header ref={headerRef} className={`hd-nav ${scrolled ? 'hd-nav-scrolled' : ''}`}>
      <div className="hd-nav-progress" style={{ width: `${scrollProgress}%` }} />

      <div className="hd-nav-inner">
        <Link to="/" className="hd-nav-logo" onClick={closeAll}>
          <Logo />
        </Link>

        <motion.nav
          className="hd-nav-links"
          ref={navLinksRef}
          onMouseLeave={resetPill}
          variants={linkStagger}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="hd-nav-pill"
            animate={pill}
            transition={{ type: 'spring', stiffness: 480, damping: 34 }}
          />

          <motion.div variants={linkItem}>
            <NavLink
              to="/"
              end
              className={({ isActive }) => `hd-nav-link ${isActive ? 'hd-nav-link-active' : ''}`}
              onMouseEnter={(e) => movePillTo(e.currentTarget)}
              onClick={closeAll}
            >
              <FaHome className="hd-nav-icon" />
              <span>Home</span>
            </NavLink>
          </motion.div>

          <motion.div
            variants={linkItem}
            className="hd-nav-dropdown"
            onMouseEnter={openMega}
            onMouseLeave={scheduleCloseMega}
          >
            <button
              type="button"
              className={`hd-nav-link ${isMegaOpen ? 'hd-nav-link-active' : ''}`}
              onMouseEnter={(e) => movePillTo(e.currentTarget)}
              onClick={(e) => (isMegaOpen ? setIsMegaOpen(false) : openMega(e))}
            >
              <FaBoxOpen className="hd-nav-icon" />
              <span>Products</span>
              <FaChevronDown className={`hd-nav-chevron ${isMegaOpen ? 'hd-nav-chevron-open' : ''}`} />
            </button>
          </motion.div>

          <motion.div variants={linkItem}>
            <NavLink
              to="/About"
              className={({ isActive }) => `hd-nav-link ${isActive ? 'hd-nav-link-active' : ''}`}
              onMouseEnter={(e) => movePillTo(e.currentTarget)}
              onClick={closeAll}
            >
              <FaInfoCircle className="hd-nav-icon" />
              <span>About</span>
            </NavLink>
          </motion.div>

          <motion.div variants={linkItem}>
            <NavLink
              to="/Contact"
              className={({ isActive }) => `hd-nav-link ${isActive ? 'hd-nav-link-active' : ''}`}
              onMouseEnter={(e) => movePillTo(e.currentTarget)}
              onClick={closeAll}
            >
              <FaEnvelope className="hd-nav-icon" />
              <span>Contact</span>
            </NavLink>
          </motion.div>
        </motion.nav>

        <div className="hd-nav-actions">
          <button
            type="button"
            className="hd-nav-icon-btn"
            aria-label="Rechercher"
            onClick={() => setIsSearchOpen(true)}
          >
            <SearchIcon active={isSearchOpen} />
          </button>

          <div className="hd-nav-cart">
            <ButtonPanier />
          </div>

          <button
            type="button"
            className="hd-nav-icon-btn hd-nav-burger"
            aria-label="Ouvrir le menu"
            aria-expanded={isMobileOpen}
            onClick={() => setIsMobileOpen(true)}
          >
            <FiMenu />
          </button>
        </div>
      </div>

      {/* Full-width mega menu — anchored to the header itself, so it can never
          clip the viewport regardless of screen size or scroll state. */}
      <AnimatePresence>
        {isMegaOpen && (
          <motion.div
            className="hd-megamenu"
            variants={megaMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onMouseEnter={cancelCloseMega}
            onMouseLeave={scheduleCloseMega}
          >
            <div className="hd-megamenu-inner">
              <div className="hd-megamenu-categories" ref={catListRef}>
                <span className="hd-megamenu-eyebrow">Parcourir</span>

                <motion.div
                  className="hd-megamenu-cat-indicator"
                  animate={catIndicator}
                  transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                />

                {categories.map((category, index) => (
                  <button
                    type="button"
                    key={category.id || category.name}
                    ref={(el) => {
                      catRowRefs.current[category.name] = el;
                    }}
                    className={`hd-megamenu-cat-row ${activeParent === category.name ? 'hd-megamenu-cat-row-active' : ''}`}
                    onMouseEnter={(e) => {
                      setActiveParent(category.name);
                      moveCatIndicatorTo(e.currentTarget);
                    }}
                    onClick={() => setActiveParent(category.name)}
                  >
                    <span className="hd-megamenu-cat-index">{String(index + 1).padStart(2, '0')}</span>
                    <span className="hd-megamenu-cat-name">{category.name}</span>
                  </button>
                ))}
              </div>

              <div className="hd-megamenu-panel">
                <AnimatePresence mode="wait">
                  {activeParent && (
                    <motion.div
                      key={activeParent}
                      variants={subListVariants}
                      initial="hidden"
                      animate="visible"
                      exit={{ opacity: 0 }}
                    >
                      <div className="hd-megamenu-panel-header">{activeParent}</div>
                      <div className="hd-megamenu-links">
                        {sousCategories.map((subCategory) => (
                          <motion.div key={subCategory.id || subCategory.name} variants={subItemVariants}>
                            <Link to={getSubcategoryPath(subCategory.name, activeParent)} className="hd-megamenu-link" onClick={closeAll}>
                              <span>{subCategory.name}</span>
                              <FiArrowRight className="hd-megamenu-link-arrow" />
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search overlay — portaled to <body> so it always covers the full
          viewport, independent of the header's own blur/stacking context. */}
      {createPortal(
        <AnimatePresence>
          {isSearchOpen && (
            <motion.div
              key="search-backdrop"
              className="hd-search-overlay-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSearchOpen(false)}
            />
          )}
          {isSearchOpen && (
            <motion.div
              key="search-panel"
              className="hd-search-overlay-panel"
              initial={{ opacity: 0, y: -24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.98 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="hd-search-overlay-label">Que recherchez-vous ?</span>
              <Search autoFocus onSubmitted={() => setIsSearchOpen(false)} />

              {categories.length > 0 && (
                <div className="hd-search-overlay-suggestions">
                  {categories.map((category) => (
                    <button
                      type="button"
                      key={category.id || category.name}
                      className="hd-search-overlay-chip"
                      onClick={() => handleChipSearch(category.name)}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              )}

              <button
                type="button"
                className="hd-search-overlay-close"
                aria-label="Fermer la recherche"
                onClick={() => setIsSearchOpen(false)}
              >
                <FaTimes /> <span>Fermer</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Mobile drawer — also portaled to <body> for the same reason. */}
      {createPortal(
        <AnimatePresence>
          {isMobileOpen && (
            <motion.div
              key="mobile-backdrop"
              className="hd-mobile-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeAll}
            />
          )}
          {isMobileOpen && (
            <motion.div
              key="mobile-drawer"
              className="hd-mobile-drawer"
              variants={drawerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className="hd-mobile-drawer-header">
                <Logo />
                <button type="button" className="hd-mobile-close" aria-label="Fermer le menu" onClick={closeAll}>
                  <FaTimes />
                </button>
              </div>

              <div className="hd-mobile-search">
                <Search onSubmitted={closeAll} />
              </div>

              <nav className="hd-mobile-links">
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) => `hd-mobile-link ${isActive ? 'hd-mobile-link-active' : ''}`}
                  onClick={closeAll}
                >
                  <FaHome className="hd-nav-icon" />
                  <span>Home</span>
                </NavLink>

                <button
                  type="button"
                  className={`hd-mobile-link hd-mobile-products-toggle ${isMobileProductsOpen ? 'hd-mobile-link-active' : ''}`}
                  onClick={toggleMobileProducts}
                >
                  <FaBoxOpen className="hd-nav-icon" />
                  <span>Products</span>
                  <FaChevronDown className={`hd-nav-chevron ${isMobileProductsOpen ? 'hd-nav-chevron-open' : ''}`} />
                </button>

                <AnimatePresence>
                  {isMobileProductsOpen && (
                    <motion.div
                      className="hd-mobile-products-panel"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <div className="hd-mobile-category-pills">
                        {categories.map((category) => (
                          <button
                            type="button"
                            key={category.id || category.name}
                            className={`hd-mobile-category-pill ${activeParent === category.name ? 'hd-mobile-category-pill-active' : ''}`}
                            onClick={() => setActiveParent(category.name)}
                          >
                            {category.name}
                          </button>
                        ))}
                      </div>
                      <div className="hd-mobile-subcats">
                        {sousCategories.map((subCategory) => (
                          <Link
                            key={subCategory.id || subCategory.name}
                            to={getSubcategoryPath(subCategory.name, activeParent)}
                            className="hd-mobile-subcat-link"
                            onClick={closeAll}
                          >
                            <FiBox className="hd-nav-icon" />
                            <span>{subCategory.name}</span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <NavLink
                  to="/About"
                  className={({ isActive }) => `hd-mobile-link ${isActive ? 'hd-mobile-link-active' : ''}`}
                  onClick={closeAll}
                >
                  <FaInfoCircle className="hd-nav-icon" />
                  <span>About</span>
                </NavLink>
                <NavLink
                  to="/Contact"
                  className={({ isActive }) => `hd-mobile-link ${isActive ? 'hd-mobile-link-active' : ''}`}
                  onClick={closeAll}
                >
                  <FaEnvelope className="hd-nav-icon" />
                  <span>Contact</span>
                </NavLink>
              </nav>

              <div className="hd-mobile-cart-row">
                <ButtonPanier />
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
};

export default Navbarr;